import {
  createContext,
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { usePostHog } from '@posthog/react';
import { ChangeActivityRequest } from '$/requests/ChangeActivityRequest.ts';
import useItineraries from '@/api/itineraries/useItineraries.ts';
import { QueryObserverResult, RefetchOptions } from '@tanstack/react-query';
import type { Itinerary } from '$/models/Itinerary.model.ts';
import useUpdateItineraryDay from '@/api/itineraries/useUpdateItineraryDay.ts';
import useUndoItineraryChange from '@/api/itineraries/useUndoItineraryChange.ts';
import useRefineTripCodySuggestion from '@/api/itineraries/useRefineTripCodySuggestion.ts';
import { RefinementRequestError } from '@/api/itineraries/invokeRefinement.ts';
import type { CodyPreset } from '$/_const/trip/CodyPreset.ts';
import type { CodyChange } from '$/requests/CodyChangeRequest.ts';
import type { CodyChangeResult } from '$/dto/CodyChangeResult.dto.ts';
import RefinementError from '$/_const/trip/RefinementError.ts';
import describeDayRefinementFailure, { type RefineDayError } from '@/utils/trip/describeDayRefinementFailure.ts';
import { useDnaRefinementContext } from '@/context/DnaRefinementContext.tsx';
import { useAuthContext } from '@/context/AuthContext.tsx';
import { useNavigate } from 'react-router-dom';
import Routes from '@/router/Routes.ts';
import { passToRoute } from '@/utils/passToRoute.ts';
import useTrips from '@/api/trips/useTrips.ts';
import type { Trip } from '$/models/Trip.model.ts';
import usePlaces from '@/api/places/usePlaces.ts';
import { Place } from '$/models/Place';
import { useToast } from '@/context/ToastContext.tsx';
import { useTranslation } from 'react-i18next';
import getItineraryChanges from '@/utils/itineraryChanges.ts';
import type { ItineraryChanges } from '@/utils/itineraryChanges.ts';
import type { ItineraryData } from '$/dto/ItineraryData.dto.ts';
import isGenerationStuck, { generationStuckAt } from '@/utils/trip/isGenerationStuck.ts';
import { type CodyReply, toAnswerAsk, toCodyReply } from '@/utils/trip/codyReply.ts';

export type CodyRefinementPhase = 'idle' | 'refining' | 'syncing';

export type CodyAsk =
  | { label: string; suggestionId: string }
  | { label: string; preset: CodyPreset }
  | { label: string; updateGuidance: string }
  | { label: string; change: CodyChange; ask: string };

export type LastChange = { id: string; summary: string };

const PLACES_WAIT_TIMEOUT = 1000 * 60 * 2;

const refinementTypeOf = (ask: CodyAsk): string => {
  if ('suggestionId' in ask) return 'suggestion';
  if ('updateGuidance' in ask) return 'custom';
  if ('change' in ask) return 'answer';

  return 'preset';
};

interface TripContextProps {
  tripId: string;
  isGenerating: boolean;
  isLoadingAction: boolean;
  setIsLoadingAction: Dispatch<SetStateAction<boolean>>;
  loadingDays: number[];
  setLoadingDays: Dispatch<SetStateAction<number[]>>;
  handleScrollToDay: (day: number) => void;
  handleRefineDay: (day: number, data: ChangeActivityRequest) => void;
  handleCodyRefinement: (ask: CodyAsk) => void;
  retryCodyRefinement: () => void;
  dismissCodyRefinementError: () => void;
  dismissCodyRefinementLimit: () => void;
  isCodyRefining: boolean;
  isCodyRefinementLimitOpen: boolean;
  codyRefinementPhase: CodyRefinementPhase;
  pendingPlacesCount: number;
  itineraryChanges: ItineraryChanges | null;
  lastRefinementAsk: string | null;
  isChangeSummaryDismissed: boolean;
  dismissChangeSummary: () => void;
  codyRefinementLabel: string | null;
  codyRefinementError: string | null;
  failedCodyRequest: string | null;
  refineDayErrors: RefineDayError[];
  itinerary: Itinerary | null;
  errorLoadingItinerary?: Error | null;
  isLoadingItinerary: boolean;
  refetchItinerary: (options?: RefetchOptions) => Promise<QueryObserverResult<Itinerary[], Error>>;
  hasErrorGeneratingItinerary: boolean;
  hasErrorGeneratingItineraryImage: boolean;
  handleUpdateTrip: () => void;
  trip: Trip | null;
  places: Place[] | null;
  isLoadingPlaces: boolean;
  errorLoadingPlaces: Error | null;
  refetchPlaces: (options?: RefetchOptions) => Promise<QueryObserverResult<Place[], Error>>;
  hasPendingPlaces: boolean;
  codyReply: CodyReply | null;
  dismissCodyReply: () => void;
  answerCodyQuestion: (option: { label: string; change: CodyChange }) => void;
  lastChange: LastChange | null;
  handleUndoLastChange: () => void;
  isUndoing: boolean;
}

const TripContext = createContext<TripContextProps>(undefined!);

type TripProviderProps = PropsWithChildren<{
  tripId: string;
}>;

export const TripProvider = ({ children, tripId }: TripProviderProps) => {
  const { canRefineTrip, getSubscriptionPage } = useAuthContext();
  const posthog = usePostHog();
  const [loadingDays, setLoadingDays] = useState<number[]>([]);
  const [isLoadingAction, setIsLoadingAction] = useState(false);
  const [refineDayErrors, setRefineDayErrors] = useState<RefineDayError[]>([]);
  const refinementInFlightRef = useRef(false);
  const [codyRefinementError, setCodyRefinementError] = useState<string | null>(null);
  const [isCodyRefinementLimitOpen, setIsCodyRefinementLimitOpen] = useState(false);
  const [codyRefinementLabel, setCodyRefinementLabel] = useState<string | null>(null);
  const [failedCodyAsk, setFailedCodyAsk] = useState<CodyAsk | null>(null);
  const [previousItineraryData, setPreviousItineraryData] = useState<ItineraryData | null>(null);
  const [lastRefinementAsk, setLastRefinementAsk] = useState<string | null>(null);
  const [isChangeSummaryDismissed, setIsChangeSummaryDismissed] = useState(false);
  const [codyReply, setCodyReply] = useState<CodyReply | null>(null);
  const [lastChange, setLastChange] = useState<LastChange | null>(null);
  const [isUndoing, setIsUndoing] = useState(false);
  const { handleGetRefinement } = useDnaRefinementContext();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { t } = useTranslation();

  const {
    data: itineraries,
    isLoading: isLoadingItinerary,
    error: errorLoadingItinerary,
    refetch: refetchItinerary,
  } = useItineraries(!!tripId, {
    trip_id: tripId,
  });

  const {
    data: trips,
    isLoading: isLoadingTrips,
    error: errorLoadingTrips,
  } = useTrips(!!tripId, {
    id: tripId,
  });

  const itinerary = useMemo(() => {
    return itineraries?.[0] ?? null;
  }, [itineraries]);

  const {
    data: places,
    isLoading: isLoadingPlaces,
    isFetching: isFetchingPlaces,
    error: errorLoadingPlaces,
    refetch: refetchPlaces,
  } = usePlaces(!!itinerary, {
    itinerary_id: itinerary?.id,
    failed_generation: false,
  });

  const hasPendingPlaces = useMemo(() => {
    if (isLoadingPlaces) return true;
    return places?.some((place) => place.is_generating) ?? false;
  }, [places, isLoadingPlaces]);

  const pendingPlacesCount = useMemo(() => places?.filter((place) => place.is_generating).length ?? 0, [places]);

  const trip = useMemo(() => {
    return trips?.[0] ?? null;
  }, [trips]);

  const [stuckCheckTime, setStuckCheckTime] = useState(() => Date.now());

  useEffect(() => {
    if (!itinerary?.is_generating) return;

    const timeout = setTimeout(
      () => setStuckCheckTime(Date.now()),
      Math.max(0, generationStuckAt(itinerary) - Date.now())
    );

    return () => clearTimeout(timeout);
  }, [itinerary]);

  const hasErrorGeneratingItinerary =
    !!itinerary?.failed_generation || (!!itinerary && isGenerationStuck(itinerary, stuckCheckTime));
  const hasErrorGeneratingItineraryImage = !!itinerary?.failed_image_generation;

  const isGenerating =
    (isLoadingItinerary || (!isLoadingItinerary && !!itinerary?.is_generating)) &&
    !errorLoadingItinerary &&
    !hasErrorGeneratingItinerary;

  const handleScrollToDay = useCallback((day: number) => {
    const el = document.querySelector(`[data-day="${day}"]`);
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, []);

  const itineraryChanges = useMemo(
    () => getItineraryChanges(previousItineraryData, itinerary?.data),
    [previousItineraryData, itinerary]
  );

  const captureItinerarySnapshot = useCallback(
    (ask: string) => {
      setPreviousItineraryData(itinerary?.data ? structuredClone(itinerary.data) : null);
      setLastRefinementAsk(ask);
      setIsChangeSummaryDismissed(false);
    },
    [itinerary]
  );

  const clearItinerarySnapshot = useCallback(() => {
    setPreviousItineraryData(null);
    setLastRefinementAsk(null);
  }, []);

  const dismissChangeSummary = useCallback(() => setIsChangeSummaryDismissed(true), []);

  const { mutateAsync: updateItineraryDay } = useUpdateItineraryDay();
  const { mutateAsync: refineTripCodySuggestion } = useRefineTripCodySuggestion();
  const { mutateAsync: undoItineraryChange } = useUndoItineraryChange();

  const handleUpdateTrip = useCallback(() => {
    if (!canRefineTrip(itinerary?.refinements_count ?? 0)) {
      console.warn('Limit on trip refinements.');
      return navigate(getSubscriptionPage());
    } else {
      navigate(
        passToRoute(Routes.TRIP__EDIT, {
          id: tripId,
        })
      );
    }
  }, [canRefineTrip, itinerary, navigate, tripId, getSubscriptionPage]);

  const handleRefineDay = useCallback(
    (day: number, data: ChangeActivityRequest) => {
      void (async () => {
        if (refinementInFlightRef.current) return;

        try {
          if (!canRefineTrip(itinerary?.refinements_count ?? 0)) {
            console.warn('Limit on trip refinements.');
            return navigate(getSubscriptionPage());
          }
          refinementInFlightRef.current = true;
          requestAnimationFrame(() => handleScrollToDay(day));
          captureItinerarySnapshot(t('tripPage.changes.ask_day', { number: day }));
          setRefineDayErrors((prev) => prev.filter((error) => error.day !== day));
          setIsLoadingAction(true);
          setLoadingDays((prev) => [...prev, day]);

          const result = await updateItineraryDay({ trip_id: tripId, day, reason: data.reason, comment: data.comment });

          if (result.outcome === 'question') {
            clearItinerarySnapshot();
            setCodyReply(toCodyReply(result));
            return;
          }

          if (result.outcome === 'not_changed') {
            clearItinerarySnapshot();
            setRefineDayErrors((prev) => [...prev, { day, error: result.message, message: result.message }]);
            return;
          }

          setLastChange(result.change_id ? { id: result.change_id, summary: result.summary } : null);
          posthog?.capture('trip_day_refined', {
            trip_id: tripId,
            day,
            reason: data.reason,
            refinements_used: (itinerary?.refinements_count ?? 0) + 1,
          });
          addToast(result.summary || t('toast.trip_day_refined'));
          void handleGetRefinement(tripId);
        } catch (error) {
          console.error('Error refining day:', day, data, error);
          clearItinerarySnapshot();
          const failure = describeDayRefinementFailure(day, error);
          setRefineDayErrors((prev) => [...prev, failure.dayError]);

          if (failure.opensPlans) {
            navigate(getSubscriptionPage());
          } else if (failure.toastKey) {
            addToast(t(failure.toastKey), 'error');
          }

          if (failure.isReported) {
            posthog?.capture('refinement_failed', { trip_id: tripId, refinement_type: 'day' });
            posthog?.captureException(error, {
              operation: 'refine_itinerary_day',
              trip_id: tripId,
              day,
            });
          }
        } finally {
          refinementInFlightRef.current = false;
          setIsLoadingAction(false);
          setLoadingDays((prev) => {
            const newState = [...prev];
            newState.pop();
            return newState;
          });
        }
      })();
    },
    [
      handleScrollToDay,
      tripId,
      updateItineraryDay,
      handleGetRefinement,
      canRefineTrip,
      itinerary,
      getSubscriptionPage,
      navigate,
      captureItinerarySnapshot,
      clearItinerarySnapshot,
      addToast,
      t,
      posthog,
    ]
  );

  const handleCodyRefinement = useCallback(
    (ask: CodyAsk) => {
      void (async () => {
        try {
          if (isLoadingAction) return;
          if (!canRefineTrip(itinerary?.refinements_count ?? 0)) {
            console.warn('Limit on trip refinements.');
            setIsCodyRefinementLimitOpen(true);
            return;
          }
          captureItinerarySnapshot(ask.label);
          setCodyRefinementError(null);
          setFailedCodyAsk(null);
          setIsLoadingAction(true);
          setCodyRefinementLabel(ask.label);

          const result: CodyChangeResult =
            'suggestionId' in ask
              ? await refineTripCodySuggestion({ trip_id: tripId, suggestion_id: ask.suggestionId })
              : 'updateGuidance' in ask
                ? await refineTripCodySuggestion({ trip_id: tripId, update_guidance: ask.updateGuidance })
                : 'change' in ask
                  ? await refineTripCodySuggestion({ trip_id: tripId, change: ask.change, ask: ask.ask })
                  : await refineTripCodySuggestion({ trip_id: tripId, preset: ask.preset });

          if (result.outcome !== 'changed') {
            clearItinerarySnapshot();
            setCodyRefinementLabel(null);
            setCodyReply(toCodyReply(result));
            return;
          }

          setLastChange(result.change_id ? { id: result.change_id, summary: result.summary } : null);
          posthog?.capture('trip_refined', {
            trip_id: tripId,
            refinement_type: refinementTypeOf(ask),
            refinements_used: (itinerary?.refinements_count ?? 0) + 1,
          });
          addToast(
            result.note === 'one_at_a_time'
              ? `${result.summary} ${t('tripPage.cody.one_at_a_time')}`
              : result.summary || t('toast.trip_refined')
          );
          void handleGetRefinement(tripId);
        } catch (error) {
          console.error('Error refining trip:', ask, error);

          clearItinerarySnapshot();
          setCodyRefinementLabel(null);

          if (error instanceof Error && error.message === 'limit') {
            setIsCodyRefinementLimitOpen(true);
            return;
          }

          const errorCode = error instanceof RefinementRequestError ? error.code : undefined;
          const isNoMaterialChange = errorCode === RefinementError.NO_MATERIAL_CHANGE;
          const isTimeout = errorCode === RefinementError.REQUEST_TIMEOUT;

          if (!isNoMaterialChange) {
            posthog?.capture('refinement_failed', { trip_id: tripId, refinement_type: refinementTypeOf(ask) });
            posthog?.captureException(error, {
              operation: 'refine_itinerary',
              trip_id: tripId,
              refinement_type: refinementTypeOf(ask),
            });
          }

          setFailedCodyAsk(ask);
          setCodyRefinementError(
            t(
              isNoMaterialChange
                ? 'tripPage.error_refine_no_change'
                : isTimeout
                  ? 'tripPage.error_refine_timeout'
                  : 'tripPage.error_refine_trip'
            )
          );
        } finally {
          setIsLoadingAction(false);
        }
      })();
    },
    [
      tripId,
      isLoadingAction,
      refineTripCodySuggestion,
      handleGetRefinement,
      canRefineTrip,
      itinerary,
      addToast,
      t,
      captureItinerarySnapshot,
      clearItinerarySnapshot,
      posthog,
    ]
  );

  const retryCodyRefinement = useCallback(() => {
    if (!failedCodyAsk || isLoadingAction) return;

    handleCodyRefinement(failedCodyAsk);
  }, [failedCodyAsk, handleCodyRefinement, isLoadingAction]);

  const dismissCodyRefinementError = useCallback(() => {
    if (isLoadingAction) return;

    setCodyRefinementError(null);
    setFailedCodyAsk(null);
  }, [isLoadingAction]);

  const dismissCodyRefinementLimit = useCallback(() => {
    setIsCodyRefinementLimitOpen(false);
  }, []);

  const dismissCodyReply = useCallback(() => setCodyReply(null), []);

  const answerCodyQuestion = useCallback(
    (option: { label: string; change: CodyChange }) => {
      if (codyReply?.kind !== 'question') return;

      setCodyReply(null);
      handleCodyRefinement(toAnswerAsk(codyReply, option));
    },
    [codyReply, handleCodyRefinement]
  );

  const handleUndoLastChange = useCallback(() => {
    void (async () => {
      if (!lastChange || isLoadingAction || isUndoing) return;

      try {
        setIsUndoing(true);
        const result = await undoItineraryChange({ trip_id: tripId, change_id: lastChange.id });

        setLastChange(null);

        if (result.outcome === 'changed') {
          clearItinerarySnapshot();
          addToast(t('tripPage.changes.undone'));
        } else {
          setCodyReply(toCodyReply(result));
        }
      } catch (error) {
        console.error('Error undoing change:', error);
        addToast(t('tripPage.changes.undo_failed'), 'error');
      } finally {
        setIsUndoing(false);
      }
    })();
  }, [lastChange, isLoadingAction, isUndoing, undoItineraryChange, tripId, clearItinerarySnapshot, addToast, t]);

  const codyRefinementPhase: CodyRefinementPhase =
    codyRefinementLabel === null ? 'idle' : isLoadingAction ? 'refining' : 'syncing';

  useEffect(() => {
    if (codyRefinementLabel === null) return;
    if (isLoadingAction || isFetchingPlaces || hasPendingPlaces) return;

    setCodyRefinementLabel(null);
  }, [codyRefinementLabel, isLoadingAction, isFetchingPlaces, hasPendingPlaces]);

  useEffect(() => {
    if (codyRefinementLabel === null || isLoadingAction) return;

    const timeout = setTimeout(() => setCodyRefinementLabel(null), PLACES_WAIT_TIMEOUT);

    return () => clearTimeout(timeout);
  }, [codyRefinementLabel, isLoadingAction]);

  const value = useMemo(
    () => ({
      tripId,
      isGenerating,
      isLoadingAction,
      setIsLoadingAction,
      loadingDays,
      setLoadingDays,
      handleScrollToDay,
      handleRefineDay,
      handleCodyRefinement,
      retryCodyRefinement,
      dismissCodyRefinementError,
      dismissCodyRefinementLimit,
      isCodyRefining: codyRefinementLabel !== null,
      isCodyRefinementLimitOpen,
      codyRefinementPhase,
      pendingPlacesCount,
      itineraryChanges,
      lastRefinementAsk,
      isChangeSummaryDismissed,
      dismissChangeSummary,
      codyRefinementLabel,
      codyRefinementError,
      failedCodyRequest: failedCodyAsk && 'updateGuidance' in failedCodyAsk ? failedCodyAsk.updateGuidance : null,
      refineDayErrors,
      itinerary: itinerary ?? null,
      errorLoadingItinerary: errorLoadingTrips || errorLoadingItinerary,
      isLoadingItinerary: isLoadingTrips || isLoadingItinerary,
      refetchItinerary,
      hasErrorGeneratingItinerary,
      hasErrorGeneratingItineraryImage,
      handleUpdateTrip,
      trip,
      places: places ?? [],
      isLoadingPlaces,
      errorLoadingPlaces,
      refetchPlaces,
      hasPendingPlaces,
      codyReply,
      dismissCodyReply,
      answerCodyQuestion,
      lastChange,
      handleUndoLastChange,
      isUndoing,
    }),
    [
      tripId,
      isGenerating,
      isLoadingAction,
      loadingDays,
      handleScrollToDay,
      handleRefineDay,
      handleCodyRefinement,
      retryCodyRefinement,
      dismissCodyRefinementError,
      dismissCodyRefinementLimit,
      isCodyRefinementLimitOpen,
      codyRefinementPhase,
      pendingPlacesCount,
      itineraryChanges,
      lastRefinementAsk,
      isChangeSummaryDismissed,
      dismissChangeSummary,
      codyRefinementLabel,
      codyRefinementError,
      failedCodyAsk,
      refineDayErrors,
      itinerary,
      errorLoadingItinerary,
      isLoadingItinerary,
      refetchItinerary,
      hasErrorGeneratingItinerary,
      hasErrorGeneratingItineraryImage,
      handleUpdateTrip,
      errorLoadingTrips,
      isLoadingTrips,
      trip,
      places,
      isLoadingPlaces,
      errorLoadingPlaces,
      refetchPlaces,
      hasPendingPlaces,
      codyReply,
      dismissCodyReply,
      answerCodyQuestion,
      lastChange,
      handleUndoLastChange,
      isUndoing,
    ]
  );

  return <TripContext.Provider value={value}>{children}</TripContext.Provider>;
};

export function useTripContext(): TripContextProps {
  const context = useContext(TripContext);

  if (typeof context === 'undefined') {
    throw new Error('useTripContext should be used within the TripContext provider!');
  }

  return context;
}
